
import { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog"
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { ImageUpload } from "@/components/ui/image-upload"
import { videoService } from "@/services/video.service"
import { useToast } from "@/hooks/use-toast"
import { Upload } from "lucide-react"

const formSchema = z.object({
    title: z.string().min(2, "Название должно содержать не менее 2 символов"),
    description: z.string().optional(),
    file: z.instanceof(File, { message: "Требуется видео файл" }),
    custom_thumbnail: z.instanceof(File).optional(),
})

interface UploadVideoDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    onSuccess: () => void
}

export function UploadVideoDialog({
    open,
    onOpenChange,
    onSuccess,
}: UploadVideoDialogProps) {
    const { toast } = useToast()
    const [isUploading, setIsUploading] = useState(false)

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            title: "",
            description: "",
            custom_thumbnail: undefined,
        },
    })

    // We need a separate handler for file input since react-hook-form doesn't control it fully
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, onChange: any) => {
        const file = e.target.files?.[0]
        if (file) {
            onChange(file)
        }
    }

    const onSubmit = async (values: z.infer<typeof formSchema>) => {
        setIsUploading(true)
        try {
            const formData = new FormData()
            formData.append("title", values.title)
            formData.append("description", values.description || "")
            formData.append("file", values.file)

            if (values.custom_thumbnail) {
                formData.append("custom_thumbnail", values.custom_thumbnail)
            }

            await videoService.uploadVideo(formData)

            toast({
                title: "Успешно",
                description: "Загрузка видео началась. Оно будет обработано в ближайшее время.",
            })
            onSuccess()
            onOpenChange(false)
            form.reset()
        } catch (error) {
            console.error(error)
            toast({
                title: "Ошибка",
                description: "Не удалось загрузить видео",
                variant: "destructive",
            })
        } finally {
            setIsUploading(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>Загрузить видео</DialogTitle>
                    <DialogDescription>
                        Загрузите видео в VdoCipher. Поддерживаемые форматы: MP4, MOV, AVI.
                    </DialogDescription>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                        <FormField
                            control={form.control}
                            name="title"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Название</FormLabel>
                                    <FormControl>
                                        <Input {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="description"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Описание</FormLabel>
                                    <FormControl>
                                        <Textarea {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="file"
                            render={({ field: { value, onChange, ...fieldProps } }) => (
                                <FormItem>
                                    <FormLabel>Видео файл</FormLabel>
                                    <FormControl>
                                        <Input
                                            {...fieldProps}
                                            type="file"
                                            accept="video/*"
                                            onChange={(event) => handleFileChange(event, onChange)}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="custom_thumbnail"
                            render={({ field: { value, onChange } }) => (
                                <FormItem>
                                    <FormLabel>Пользовательская миниатюра (опционально)</FormLabel>
                                    <FormControl>
                                        <ImageUpload
                                            value={value}
                                            onChange={onChange}
                                            label="Загрузить миниатюру"
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <DialogFooter>
                            <Button type="submit" disabled={isUploading}>
                                {isUploading ? (
                                    <>
                                        <Upload className="mr-2 h-4 w-4 animate-bounce" />
                                        Загрузка...
                                    </>
                                ) : (
                                    "Загрузить"
                                )}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}
